/**
 * JoinButton — handles public / private / restricted join states with a
 * satisfying join animation and a lightweight leave/cancel confirmation.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Clock, Lock } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { useCommunities } from './context/CommunitiesContext';

export default function JoinButton({ community, size = 'md', className = '', ...rest }) {
  const { joinCommunity, leaveCommunity, cancelRequest } = useCommunities();
  const [justJoined, setJustJoined] = useState(false);
  const [joining, setJoining] = useState(false);
  const sizeCls = size === 'sm' ? 'px-3.5 py-1.5 text-xs' : 'px-5 py-2.5 text-sm';

  if (community.visibility === 'restricted' && community.joinStatus !== 'joined') {
    return (
      <span {...rest} className={cx('inline-flex items-center gap-1.5 rounded-full border border-line text-subtle', sizeCls, className)}>
        <Lock className="h-3.5 w-3.5" /> Invite Only
      </span>
    );
  }

  if (community.joinStatus === 'joined') {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            {...rest}
            type="button"
            className={cx('group inline-flex items-center gap-1.5 rounded-full border border-coral/40 bg-coral/10 font-semibold text-coral transition-colors hover:border-coral hover:bg-coral hover:text-[#FFF7F0]', sizeCls, className)}
          >
            <span className="group-hover:hidden inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> Joined</span>
            <span className="hidden group-hover:inline">Leave?</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="center" className="w-56 rounded-xl border-line bg-card p-3 text-center text-ink shadow-xl">
          <p className="text-sm">Leave <span className="font-semibold">{community.name}</span>?</p>
          <div className="mt-3 flex justify-center gap-2">
            <button
              type="button"
              onClick={() => { leaveCommunity(community.id); toast(`Left ${community.name}`); }}
              className="rounded-full border border-coral/40 px-3 py-1.5 text-xs font-semibold text-coral hover:bg-coral/10"
            >
              Leave
            </button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  if (community.joinStatus === 'pending') {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button {...rest} type="button" className={cx('inline-flex items-center gap-1.5 rounded-full border border-line text-subtle transition-colors hover:border-coral/40 hover:text-coral', sizeCls, className)}>
            <Clock className="h-3.5 w-3.5" /> Request Pending
          </button>
        </PopoverTrigger>
        <PopoverContent align="center" className="w-56 rounded-xl border-line bg-card p-3 text-center text-ink shadow-xl">
          <p className="text-sm">Cancel your request to join <span className="font-semibold">{community.name}</span>?</p>
          <div className="mt-3 flex justify-center gap-2">
            <button
              type="button"
              onClick={() => { cancelRequest(community.id); toast('Request canceled'); }}
              className="rounded-full border border-coral/40 px-3 py-1.5 text-xs font-semibold text-coral hover:bg-coral/10"
            >
              Cancel Request
            </button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  const handleJoin = async () => {
    if (joining) return;
    setJoining(true);
    try {
      await joinCommunity(community.id);
      if (community.visibility === 'private') {
        toast(`Request sent to ${community.name}`);
      } else {
        setJustJoined(true);
        toast(`Joined ${community.name} — welcome!`);
        setTimeout(() => setJustJoined(false), 700);
      }
    } catch (error) {
      toast(error.message || `We could not join ${community.name}.`);
    } finally {
      setJoining(false);
    }
  };

  return (
    <motion.button
      {...rest}
      type="button"
      onClick={handleJoin}
      disabled={joining}
      whileTap={{ scale: 0.96 }}
      animate={justJoined ? { scale: [1, 1.08, 1] } : {}}
      className={cx('inline-flex items-center gap-1.5 rounded-full bg-coral-fill font-semibold text-[#FFF7F0] shadow-soft transition-transform hover:-translate-y-0.5', sizeCls, className)}
    >
      <AnimatePresence mode="wait" initial={false}>
        {justJoined ? (
          <motion.span key="joined" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-1.5">
            <Check className="h-3.5 w-3.5" /> Joined
          </motion.span>
        ) : (
          <motion.span key="join" initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}>
            {community.visibility === 'private' ? 'Request to Join' : 'Join'}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
}
